import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import getSchema from "../../lib/api/getSchema";
import notificationState from "../../states/notificationState";
import AccessControl from "../functional/AccessControl";
import DeleteRecordButton from "../input/DeleteRecordButton";
import SaveToExcelButton from "../input/SaveToExcelButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import MomSysMstMaterialExcelUploadButton from "../input/MomSysMstMaterialExcelUploadButton";
import MomSysMstMaterialCopyCreateButton from "../input/MomSysMstMaterialCopyCreateButton";
import MomSysMstMaterialCreateButton from "../input/MomSysMstMaterialCreateButton";
import MomSysMstMaterialUpdateButton from "../input/MomSysMstMaterialUpdateButton";
import { cloneDeep } from "lodash";
import { Tooltip, toggleClass, } from '@grapecity/wijmo';
import "./gridEvent.css"

function MomSysMstMaterial(props) {
    const { id, masterGridSetting } = props;

    const [schema, setSchema] = useState();
    const [Api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [onSelectGrid, setOnSelectGrid] = useState();
    const [modifiedSchema, setModefiedSchema] = useState();
    const [isEmpty, setIsEmpty] = useState();

    const setNotification = useSetRecoilState(notificationState);

    const { t } = useTranslation();

    useEffect(() => {
        let mounted = true;
        async function schemaSetting() {
            let schema = await getSchema("MomSysMstMaterial");

            if ( mounted ) {
                schema.columns.forEach(column => schemaColumnSetting(column));
                setSchema(schema);

                let modifiedSchema = cloneDeep(schema);
                modifiedSchema.columns.forEach(column => columnSetting(column));
                setModefiedSchema(modifiedSchema);
            }

            function schemaColumnSetting(column) {
                if(column.name === "excelFile") {
                    column.hideInForm = true;
                    column.required = false;
                }
            }
            
            function columnSetting(column) {
                if(column.key === "PRI") {
                    column.defaultValue = "pri";
                }
    
                if(column.name !== "excelFile") {
                    column.hideInForm = true;
                    column.required = false;
                } else {
                    column.hideInForm = false;
                }
            }
        }

        schemaSetting();

        return (() => {
            mounted = false;
        });
    }, []);

    useEffect(() => {
        let tooltip = new Tooltip();
        (async () => {
            if (grid) {
                grid.selectionChanged.addHandler((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        masterGridSetting(grid.selectedItems[0]);
                        setOnSelectGrid(grid.selectedItems[0]);
                    } else {
                        masterGridSetting([]);
                        setOnSelectGrid(undefined);
                    };
                });

                grid.itemsSourceChanged.addHandler((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        masterGridSetting(grid.selectedItems[0]);
                        setOnSelectGrid(grid.selectedItems[0]);
                    } else {
                        masterGridSetting([]);
                        setOnSelectGrid(undefined);
                    };
                });

                grid.formatItem.addHandler((flex, element) => {
                    if (element.panel === flex.cells) {
                        let item = flex.rows[element.row].dataItem
                        let binding = flex.columns[element.col].binding
                        let data = item.description ? item.description : null;
                        toggleClass(element.cell, 'wj-has-notes', binding === "description" && data !== null );
                        if(binding === "description" && data !== null) {
                            tooltip.setTooltip(element.cell, `<h1>${data}<h1>`);
                        }
                    }
                });
            }
        })();
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grid]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };
    
    const onLoadData = (data) => {
        setIsEmpty(data.length < 1 ? true : false)
    }

    const onModifyUrl = (url) => {
        return `${url}?excelData`;
    };

    return (
        <>
            <BasicCrudScreenType
                id={id}
                table="MomSysMstMaterial"
                view="MomSysZvwMaterial"
                onApiInitialized={onApiInitialized}
                onInitialized={onInitialized}
                headerTitle={t("term.standard")}
                onLoadData={onLoadData}
                noCreate
                noEdit
                noDelete
                noExcel
                onAppendButtonInFrontOfSearch={() => (
                    <>
                        <BasicCrudScreenActionItemLayout>
                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <MomSysMstMaterialExcelUploadButton
                                    id="MomSysMstMaterialExcelUploadButton"
                                    createButtonName="excelUpload"
                                    schema={modifiedSchema}
                                    refresh={Api}
                                    onSuccess={onSuccess}
                                    modifyUrlCallback={(url) => onModifyUrl(url)}
                                />
                            </AccessControl>

                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <MomSysMstMaterialCopyCreateButton
                                    id = "MomSysMstMaterialCopyCreateButton"
                                    schema = {schema}
                                    refresh = {Api}
                                    onSelectGrid = {onSelectGrid}
                                    onSuccess = {onSuccess}
                                    createButtonName = {"materialCopyCreate"}
                                    disabled={isEmpty ? true : false}
                                />
                            </AccessControl>

                            <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                                <MomSysMstMaterialCreateButton
                                    id = "MomSysMstMaterialCreateButton"
                                    schema = {schema}
                                    refresh = {Api}
                                    onSuccess = {onSuccess}
                                />
                            </AccessControl>

                            <AccessControl accessTarget={schema && schema.name} accessMethod={schema && (schema.updateWithPatch ? "PATCH" : "PUT")}>
                                <MomSysMstMaterialUpdateButton
                                    id = "MomSysMstMaterialUpdateButton"
                                    schema = {schema}
                                    refresh = {Api}
                                    onSuccess = {onSuccess}
                                    grid={grid}
                                    disabled={isEmpty ? true : false}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod="DELETE"
                            >
                                <DeleteRecordButton
                                    id={id}
                                    schema={schema}
                                    grid={grid}
                                    refresh={Api}
                                    color="secondary"
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    onPartialFailure={onNotifyWarning}
                                    disabled={isEmpty ? true : false}
                                />
                            </AccessControl>

                            <AccessControl
                                accessTarget={schema && schema.name}
                                accessMethod="SAVETOEXCEL"
                            >
                                <SaveToExcelButton
                                    id={id}
                                    grid={grid}
                                    view="MomSysZvwMaterial"
                                    color="primary"
                                />
                            </AccessControl>
                        </BasicCrudScreenActionItemLayout>
                    </>
                )}
            />
        </>
    );
}

export default MomSysMstMaterial;
